import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
// import { ProductContextModel } from "@insite/client-framework/Components/ProductContext"; // Poolcorp Custom Code
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import getColor from "@insite/mobius/utilities/getColor";
import {
    getPolicyCanSeeNormalPrices,
    getPolicyCanSeePrices,
    getPolicyCanSeeRetailPrices,
    getShowNormalPrice,
} from "@store/Store/Context/ContextSelectors";
import { getHideAllPricesModeOn } from "@store/Store/Data/HideAllPricesMode/HideAllPricesModeSelector";
import { getRetailModeActive, getRetailModeOn } from "@store/Store/Data/RetailMode/RetailModeSelector";
import { contentSpacingStyles } from "@store/theme/common/mixins";
import { loadingDotsAnimation } from "@store/theme/common/mixins/typography";
import { TYPOGRAPHY } from "@store/theme/design";
/* Poolcorp Custom Code */

interface OwnProps {
    product?: ProductContextModel | CartLineModel; // Poolcorp Custom Code
    currencySymbol?: string;
    label?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
    // Poolcorp Custom Code
    showSingle?: boolean;
    showBoth?: boolean;
    showNormalOnly?: boolean;
    showRetailOnly?: boolean;
    showRetailLabel?: boolean;
    isCondensed?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    // Poolcorp Custom Code
    isRetailModeActive: getRetailModeActive(state),
    isHideAllPricesModeOn: getHideAllPricesModeOn(state),
    isRetailModeOn: getRetailModeOn(state),
    policyCanSeePrices: getPolicyCanSeePrices(state),
    policyCanSeeNormalPrices: getPolicyCanSeeNormalPrices(state),
    policyCanSeeRetailPrices: getPolicyCanSeeRetailPrices(state),
    showNormalPrice: getShowNormalPrice(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext; // Poolcorp Custom Code

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    // Poolcorp Custom Code
    normalPriceWrapper?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    retailPriceWrapper?: InjectableCss;
    retailPrice?: TypographyPresentationProps;
    savingPriceText?: TypographyPresentationProps;
    condensedPriceLabelText?: TypographyPresentationProps;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    productPriceWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;

            > section:first-child {
                margin-right: 1em;
            }
        `,
    },
    normalPriceWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: auto;
        `,
    },
    wrapper: {},
    priceLabelText: {
        variant: "legend",
    },
    condensedPriceLabelText: {
        variant: "legend",
        css: css`
            padding: 0;
            text-align: right;
        `,
    },
    priceWrapper: {
        css: css`
            word-wrap: break-word;
        `,
    },
    price: {
        signInText: { weight: TYPOGRAPHY.weight.bold },
        priceText: {
            weight: TYPOGRAPHY.weight.bold,
            size: TYPOGRAPHY.size.largest,
        },
        errorText: { weight: TYPOGRAPHY.weight.bold },
        realTimeText: {
            css: css`
                ${loadingDotsAnimation()}
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                weight: TYPOGRAPHY.weight.base,
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    retailPriceWrapper: {
        css: css`
            ${contentSpacingStyles("0.25em", null)}
        `,
    },
    retailPrice: {
        size: TYPOGRAPHY.size.smaller,
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            color: ${getColor("text.accent")};
        `,
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: TYPOGRAPHY.weight.bold,
    },
    savingPriceText: {
        italic: true,
        size: TYPOGRAPHY.size.tiny,
        lineHeight: TYPOGRAPHY.lineHeight.smaller,
        color: "danger",
        css: css`
            display: block;
            text-decoration: line-through;
            margin-top: 0.2em;
        `,
    },
    savingsMessageText: {
        italic: true,
        size: TYPOGRAPHY.size.tiny,
        lineHeight: TYPOGRAPHY.lineHeight.small,
        color: "text.accent",
        css: css`
            margin-bottom: 0.2em;
        `,
    },
    quoteMessage: {
        text: { weight: TYPOGRAPHY.weight.bold },
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product, // Poolcorp Custom Code
    currencySymbol,
    canSeePrices,
    showLabel = true,
    label,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    showSalePriceLabel = true,
    // Poolcorp Custom Code
    productContext,
    isRetailModeActive,
    isHideAllPricesModeOn,
    isRetailModeOn,
    showSingle = false,
    showBoth = false,
    showNormalOnly = false,
    showRetailOnly = false,
    showRetailLabel = true,
    policyCanSeePrices,
    policyCanSeeNormalPrices,
    policyCanSeeRetailPrices,
    showNormalPrice,
    isCondensed = false,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));

    /* Poolcorp Custom Code */
    if ((!showPricing && !isCheckoutReviewAndSubmitPage) || (!policyCanSeePrices && !policyCanSeeRetailPrices) || (!product && !productContext.product)) { // Poolcorp Custom Code - not using showPricing
        return null;
    }

    const pricingData = product || productContext;
    /* Poolcorp Custom Code */

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    let pricing;
    let retailPrice; // Poolcorp Custom Code
    let hasPrice = true; // Poolcorp Custom Code
    if ("product" in pricingData) {
        pricing = pricingData.productInfo.pricing;
        retailPrice = pricingData.productInfo.pricing?.additionalResults?.retailPrice; // Poolcorp Custom Code
        hasPrice = Boolean(pricingData.productInfo.pricing?.extendedUnitNetPriceDisplay); // Poolcorp Custom Code
    } else {
        pricing = pricingData.pricing;
        retailPrice = pricingData.pricing?.additionalResults?.retailPrice; // Poolcorp Custom Code
        hasPrice = Boolean(pricingData.pricing?.extendedUnitNetPriceDisplay); // Poolcorp Custom Code
    }
    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};
    const unavailableMessage = siteMessage("RealTimePricing_PriceLoadFailed").toString(); // Poolcorp Custom Code

    if (!canSeePrices) {
        /* Poolcorp Custom Code */
        const message = siteMessage("Pricing_SignInForPrice").toString();

        return message ? (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText} data-test-selector="productPrice_PricingMessage">
                    {message}
                </Typography>
            </StyledWrapper>
        ) : null;
        /* Poolcorp Custom Code */
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        /* Poolcorp Custom Code */
        return unavailableMessage ? (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                    {unavailableMessage}
                </Typography>
            </SectionWrapper>
        ) : null;
        /* Poolcorp Custom Code */
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.price?.unitOfMeasure} // Poolcorp Custom Fix
                    />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    /* Poolcorp Custom Code */
    if (!pricing) {
        return (
            <Typography {...priceStyles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                {currencySymbol}
            </Typography>
        );
    }

    const unitOfMeasureOutput =
        hasPrice ? (
            <UnitOfMeasure
                unitOfMeasure={unitOfMeasure}
                showUnitOfMeasure={showUnitOfMeasure}
                styles={styles.price?.unitOfMeasure} // Poolcorp Custom Fix
            />
        ) : null;

    const baseUnitOfMeasureOutput =
        showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData ? (
            <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
            </Typography>
        ) : null;

    const packDescriptionOutput =
        packDescription && showPack ? (
            <StyledWrapper {...styles.packWrapper}>
                <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
            </StyledWrapper>
        ) : null;

    const retailPriceComponent = policyCanSeeRetailPrices ? (
        <SectionWrapper {...styles.retailPriceWrapper}>
            {showRetailLabel && !isRetailModeOn ? <Typography {...(isCondensed ? styles.condensedPriceLabelText : styles.priceLabelText)}>{translate("Retail")}</Typography> : null}
            <StyledWrapper {...styles.priceWrapper}>
                {retailPrice ? (
                    <Typography {...(!isRetailModeOn ? styles.retailPrice : styles.price?.priceText)} data-test-selector="productPrice_unitNetPrice">
                        {hasPrice ? retailPrice : unavailableMessage}
                    </Typography>
                ) : (
                    <Typography {...priceStyles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                        {currencySymbol}
                    </Typography>
                )}
            </StyledWrapper>
            {packDescriptionOutput}
        </SectionWrapper>
    ) : null;

    const unitNetPrice = qtyOrdered! >= ((pricing.actualBreakPrices && pricing.actualBreakPrices.length > 0 && pricing.actualBreakPrices[0]?.breakQty) || 0)
        ? getUnitNetPrice(pricing, qtyOrdered || 1).price
        : pricing.unitNetPrice;

    const unitNetPriceDisplay = qtyOrdered! >= ((pricing.actualBreakPrices && pricing.actualBreakPrices.length > 0 && pricing.actualBreakPrices[0]?.breakQty) || 0)
        ? getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay
        : pricing.unitNetPriceDisplay;

    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered || 1);

    const hasPriceSavings = unitNetPrice < unitListPrice;

    const normalPriceComponent = policyCanSeeNormalPrices ? (
        <SectionWrapper {...styles.normalPriceWrapper}>
            <StyledWrapper {...styles.priceWrapper}>
                {pricing ? (
                    <>
                        {hasPriceSavings && (
                            <Typography {...styles.savingPriceText}>
                                {unitListPriceDisplay}
                            </Typography>
                        )}
                        <Typography {...priceStyles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {unitNetPriceDisplay}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography {...priceStyles.realTimeText} data-test-selector="productPrice_unitNetPrice_CurrencySymbol">
                            {currencySymbol}
                        </Typography>
                    </>
                )}
                {isInvalidPrice && showInvalidPriceMessage && (
                    <Typography as="p" {...styles.invalidPriceText}>
                        {translate("Invalid Price")}
                    </Typography>
                )}
            </StyledWrapper>
            {baseUnitOfMeasureOutput}
            {packDescriptionOutput}
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )}
        </SectionWrapper>
    ) : null;

    let priceOutput = normalPriceComponent;

    if (isRetailModeActive) {
        priceOutput = (
            <>
                {showNormalPrice ? normalPriceComponent : null}
                {retailPriceComponent}
            </>
        );

        if (!hasPrice || showSingle) {
            priceOutput = isRetailModeOn ? retailPriceComponent : normalPriceComponent;
        }

        if (showNormalOnly) {
            priceOutput = normalPriceComponent;
        }

        if (showRetailOnly) {
            priceOutput = retailPriceComponent;
        }

        if (showBoth) {
            priceOutput = hasPrice ? (
                <>
                    {showNormalPrice ? normalPriceComponent : null}
                    {retailPriceComponent}
                </>
            ) : (
                normalPriceComponent
            );
        }
    }
    if (isHideAllPricesModeOn){
        priceOutput = null
    }

    return priceOutput ?
        isCondensed ? (
                <StyledWrapper {...styles.productPriceWrapper}>{priceOutput}</StyledWrapper>
            ) : (
                <StyledWrapper>{priceOutput}</StyledWrapper>
            )
        : null;
    /* Poolcorp Custom Code */
};

/* Poolcorp Custom Code
interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }
    return (
        <>
            {pricing ? (
                <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                    {salePriceLabelPrefix}
                    {enableVat && displayWithVat
                        ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                        : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                </Typography>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                    {currencySymbol}
                </Typography>
            )}
            {unitOfMeasure && showUnitOfMeasure && (
                <Typography {...styles.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
                    &nbsp;/&nbsp;{unitOfMeasure}
                </Typography>
            )}
            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};
*/

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            &nbsp;/&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(withProductContext(ProductPrice)); // Poolcorp Custom Code
